import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import Sidebar from "@/components/layout/Sidebar";
import Topbar from "@/components/layout/Topbar";
import CommandPalette from "@/components/layout/CommandPalette";
import { NAV } from "@/constants/nav";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import NexreqAIPanel from "@/components/nexreq-ai/NexreqAIPanel";

function titleFor(pathname) {
  if (pathname === "/") return "Command Center";
  const matches = NAV.filter(
    (n) =>
      n.path !== "/" &&
      (pathname === n.path || pathname.startsWith(n.path + "/")),
  );
  if (matches.length)
    return matches.sort((a, b) => b.path.length - a.path.length)[0].label;
  const fallback = NAV.find(
    (n) => n.path !== "/" && pathname.startsWith(n.path),
  );
  return fallback ? fallback.label : "Nexreq ATS";
}

export default function AppLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [urgentAnnouncement, setUrgentAnnouncement] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const pageTitle =
    user?.role === "founder" && location.pathname === "/"
      ? "Executive Dashboard"
      : titleFor(location.pathname);

  useEffect(() => {
    api
      .get("/communications/urgent/current")
      .then(({ data }) => setUrgentAnnouncement(data.item || null))
      .catch(() => setUrgentAnnouncement(null));
  }, []);

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar
        collapsed={mobileNavOpen ? false : collapsed}
        onToggle={() => setCollapsed((c) => !c)}
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
      />
      {mobileNavOpen && (
        <button
          aria-label="Close navigation"
          data-testid="mobile-nav-backdrop"
          className="fixed inset-0 z-20 bg-black/35 md:hidden"
          onClick={() => setMobileNavOpen(false)}
        />
      )}
      <div className="flex-1 min-w-0 flex flex-col">
        <Topbar
          onOpenPalette={() => setPaletteOpen(true)}
          onOpenMobileNav={() => setMobileNavOpen(true)}
          title={pageTitle}
        />
        {urgentAnnouncement && (
          <button
            type="button"
            onClick={() =>
              navigate(`/communications?announcement=${urgentAnnouncement.id}`)
            }
            data-testid="urgent-announcement-banner"
            className="flex items-center gap-2 border-b border-red-200 bg-red-50 px-4 py-2 text-left text-sm text-red-950 transition-colors hover:bg-red-100"
          >
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span className="font-semibold">Urgent:</span>
            <span className="truncate">{urgentAnnouncement.title}</span>
          </button>
        )}
        <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
      <CommandPalette open={paletteOpen} setOpen={setPaletteOpen} />
      <NexreqAIPanel />
    </div>
  );
}
